import { useState, useEffect, useCallback, useRef } from 'react';
import type { PriceAlert, CoinData } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';

const PRICE_ALERTS_STORAGE_KEY = 'cryptoscopePriceAlerts';
const ALERT_CHECK_INTERVAL = 60000; // Check alerts every minute
const ALERT_DURATION_DAYS = 30; // Alerts expire after 30 days

export function usePriceAlerts(cryptoData: CoinData[]) {
  const [alerts, setAlerts] = useState<PriceAlert[]>(() => {
    const saved = localStorage.getItem(PRICE_ALERTS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Filter out expired alerts
        const now = Date.now();
        return parsed.filter((alert: PriceAlert) => alert.expiresAt > now && !alert.triggered);
      } catch {
        return [];
      }
    }
    return [];
  });

  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Save alerts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(PRICE_ALERTS_STORAGE_KEY, JSON.stringify(alerts));
  }, [alerts]);

  // Check alerts against current prices
  const checkAlerts = useCallback(() => {
    if (!cryptoData || cryptoData.length === 0) return;

    setAlerts(prev => {
      const updated = prev.map(alert => {
        // Skip if already triggered or expired
        if (alert.triggered || alert.expiresAt <= Date.now()) {
          return alert;
        }

        const coin = cryptoData.find(c => c.id === alert.coinId);
        if (!coin) return alert;

        const currentPrice = coin.current_price;
        let shouldTrigger = false;

        if (alert.direction === 'above' && currentPrice >= alert.targetPrice) {
          shouldTrigger = true;
        } else if (alert.direction === 'below' && currentPrice <= alert.targetPrice) {
          shouldTrigger = true;
        }

        if (shouldTrigger) {
          // Trigger the alert
          triggerAlert(alert, currentPrice);
          return {
            ...alert,
            triggered: true,
            triggeredAt: Date.now(),
            currentPrice: currentPrice
          };
        }

        return {
          ...alert,
          currentPrice: currentPrice
        };
      });

      return updated;
    });
  }, [cryptoData]);

  // Trigger an alert notification
  const triggerAlert = (alert: PriceAlert, currentPrice: number) => {
    const priceStr = formatCurrency(currentPrice, alert.currency);
    const targetStr = formatCurrency(alert.targetPrice, alert.currency);
    const direction = alert.direction === 'above' ? 'reached' : 'dropped to';

    const notificationOptions: NotificationOptions = {
      body: `${alert.coinName} (${alert.coinSymbol}) ${direction} ${targetStr}! Current price: ${priceStr}`,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `price-alert-${alert.id}`,
      requireInteraction: true,
      vibrate: [200, 100, 200],
    };

    // Request notification permission if not already granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`🔔 Price Alert: ${alert.coinName}`, notificationOptions);
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(`🔔 Price Alert: ${alert.coinName}`, notificationOptions);
        }
      });
    }

    // Show in-app notification if possible (console for now)
    console.log(`🔔 Price Alert: ${alert.coinName} (${alert.coinSymbol}) ${direction} ${targetStr}!`);
  };

  // Set up interval to check alerts
  useEffect(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    checkIntervalRef.current = setInterval(() => {
      checkAlerts();
    }, ALERT_CHECK_INTERVAL);

    // Initial check
    checkAlerts();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkAlerts]);

  // Create a new price alert
  const createAlert = useCallback(async (
    coinId: string,
    coinSymbol: string,
    coinName: string,
    targetPrice: number,
    direction: 'above' | 'below',
    currency: string,
    emailNotification: boolean = false
  ): Promise<PriceAlert | null> => {
    // Request payment for creating alert
    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      console.error('Payment system not available');
      return null;
    }

    try {
      let totalAmount = 100; // Base price for alert (100 satoshis)
      
      if (emailNotification) {
        totalAmount += 50; // Additional 50 satoshis for email notifications
      }

      const paymentResult = await window.bsvPayments.requestPayment('PRICE_ALERT', totalAmount);
      
      if (!paymentResult) {
        console.log('Price alert payment cancelled or failed');
        return null;
      }

      // If email notification was requested, make additional payment
      if (emailNotification) {
        const emailPayment = await window.bsvPayments.requestPayment('PRICE_ALERT_EMAIL', 50);
        if (!emailPayment) {
          // If email payment fails, still create the alert but without email
          emailNotification = false;
        }
      }

      const coin = cryptoData.find(c => c.id === coinId);
      const currentPrice = coin?.current_price || 0;

      const newAlert: PriceAlert = {
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        coinId,
        coinSymbol,
        coinName,
        targetPrice,
        direction,
        currentPrice,
        currency,
        createdAt: Date.now(),
        expiresAt: Date.now() + (ALERT_DURATION_DAYS * 24 * 60 * 60 * 1000),
        triggered: false,
        emailNotification
      };

      setAlerts(prev => [...prev, newAlert]);
      return newAlert;
    } catch (error) {
      console.error('Error creating price alert:', error);
      return null;
    }
  }, [cryptoData]);

  // Delete an alert
  const deleteAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  }, []);

  // Get alerts for a specific coin
  const getAlertsForCoin = useCallback((coinId: string) => {
    return alerts.filter(alert => alert.coinId === coinId && !alert.triggered);
  }, [alerts]);

  // Check if user has notification permission
  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }, []);

  // Get all active (non-triggered, non-expired) alerts
  const getActiveAlerts = useCallback(() => {
    const now = Date.now();
    return alerts.filter(alert => !alert.triggered && alert.expiresAt > now);
  }, [alerts]);

  // Get all triggered alerts
  const getTriggeredAlerts = useCallback(() => {
    return alerts.filter(alert => alert.triggered);
  }, [alerts]);

  return {
    alerts: getActiveAlerts(),
    triggeredAlerts: getTriggeredAlerts(),
    allAlerts: alerts,
    createAlert,
    deleteAlert,
    getAlertsForCoin,
    requestNotificationPermission,
    checkAlerts
  };
}

